// utils
import * as BREAKPOINTS from '@zola/zola-ui/src/lib/ui/breakpoints';
import { COLORS3 as COLORS } from '@zola/zola-ui/src/styles/emotion';
import insertInArrayIf from '@zola-helpers/client/dist/es/util/insertInArrayIf';
import staticFlags, { UPDATED_TILES_WEDDING_PLANNING } from 'libs/universal/utils/staticFlags';

// assets
import AdviceMobile from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/advice.jpg';
import AppMobile from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/app.jpg';
import ContactCollectorMobile from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/contact_collector.jpg';
import GuestListMobile from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/guest_list.jpg';

import AdviceMobileWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/advice.webp';
import AppMobileWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/app.webp';
import ContactCollectorMobileWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/contact_collector.webp';
import GuestListMobileWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/mobile/guest_list.webp';

import AdviceDesktop from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/advice.jpg';
import AppDesktop from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/app.jpg';
import ContactCollectorDesktop from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/contact_collector.jpg';
import GuestListDesktop from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/guest_list.jpg';

import AdviceDesktopWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/advice.webp';
import AppDesktopWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/app.webp';
import ContactCollectorDesktopWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/contact_collector.webp';
import GuestListDesktopWebp from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/desktop/guest_list.webp';

import AdviceForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/advice.png';
import AppForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/app.png';
import ChecklistForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/checklist.png';
import CommunityForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/community.png';
import ContactCollectorForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/contact_collector.png';
import GuestListForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/guest_list.png';
import RegistryForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/registry.png';
import TeamZForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/team_z.png';
import WebsiteForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/website.png';
import BudgetForeground from '../../../../../public/static-preauth/images/WeddingPlanningLP/grid/foreground/budget.png';

const communityTile = {
  href: '/community',
  header: 'Zola Community',
  description: 'Celebrate, share, and vent with other engaged couples.',
  backgroundColor: COLORS.BAY_100,
  foregroundImageSrc: CommunityForeground,
};

type PlanningGridItem = {
  href: string;
  header: string;
  description: string;
  backgroundColor?: string;
  foregroundImageSrc: string;
  backgroundImages?: {
    webp: { url: string; maxBreakpoint?: string; minBreakpoint?: string }[];
    jpg: { url: string; maxBreakpoint?: string; minBreakpoint?: string }[];
  };
  excludeOnMobile?: boolean;
};

const content: PlanningGridItem[] = [
  {
    href: '/wedding-planning/website',
    header: 'Free wedding website',
    description: 'The free (and beautiful!) home for all of your wedding details.',
    backgroundColor: COLORS.COVE_100,
    foregroundImageSrc: WebsiteForeground,
  },
  {
    href: '/wedding/onboard/wedding-guest-list',
    header: 'Contact Collector',
    description: 'Collect all your guests’ details with one simple link.',
    backgroundImages: {
      webp: [
        {
          url: ContactCollectorMobileWebp,
          maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString(),
        },
        { url: ContactCollectorDesktopWebp, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
      jpg: [
        { url: ContactCollectorMobile, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: ContactCollectorDesktop, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
    },
    foregroundImageSrc: ContactCollectorForeground,
  },
  ...insertInArrayIf(!staticFlags.get(UPDATED_TILES_WEDDING_PLANNING), communityTile),
  ...insertInArrayIf(staticFlags.get(UPDATED_TILES_WEDDING_PLANNING), {
    href: '/wedding-budget',
    header: 'Budget tracker',
    description: 'Customize your wedding budget with our free tool.',
    backgroundColor: COLORS.MARINE_100,
    foregroundImageSrc: BudgetForeground,
  }),
  {
    href: 'https://zola.app.link/SZZ28JV0PS',
    header: 'Free planning app',
    description: 'Explore exclusive features, and plan on the go.',
    backgroundImages: {
      webp: [
        { url: AppMobileWebp, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: AppDesktopWebp, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
      jpg: [
        { url: AppMobile, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: AppDesktop, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
    },
    foregroundImageSrc: AppForeground,
  },
  {
    href: '/wedding-planning/checklist',
    header: 'Wedding checklist',
    description: 'A simple way to manage all the tasks along the way.',
    backgroundColor: COLORS.ROSE_100,
    foregroundImageSrc: ChecklistForeground,
  },
  {
    href: '/wedding-planning/guests',
    header: 'Guest list manager',
    description: 'You choose your guests, we’ll do the rest.',
    backgroundImages: {
      webp: [
        { url: GuestListMobileWebp, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: GuestListDesktopWebp, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
      jpg: [
        { url: GuestListMobile, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: GuestListDesktop, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
    },
    foregroundImageSrc: GuestListForeground,
  },
  ...insertInArrayIf(!staticFlags.get(UPDATED_TILES_WEDDING_PLANNING), {
    href: '/contact-us',
    header: 'Team-Z',
    description: 'Meet our advisors for all things wedding planning.',
    backgroundColor: COLORS.FOREST_100,
    foregroundImageSrc: TeamZForeground,
  }),
  ...insertInArrayIf(staticFlags.get(UPDATED_TILES_WEDDING_PLANNING), communityTile),
  {
    href: '/expert-advice',
    header: 'Expert Advice',
    description: 'Wedding planning questions? We’ve got the answers.',
    backgroundImages: {
      webp: [
        { url: AdviceMobileWebp, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: AdviceDesktopWebp, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
      jpg: [
        { url: AdviceMobile, maxBreakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString() },
        { url: AdviceDesktop, minBreakpoint: BREAKPOINTS.SCREEN_SM.toString() },
      ],
    },
    foregroundImageSrc: AdviceForeground,
  },
  {
    href: '/wedding-registry',
    header: 'Wedding registry',
    description: 'The wedding registry that gifts you more.',
    backgroundColor: COLORS.WINE_100,
    foregroundImageSrc: RegistryForeground,
    excludeOnMobile: true,
  },
];

export default content;
